import type {
  CommonResponseSliceTeacherResponse,
  CommonResponseTeacherResponse,
  CreateTeacherData,
  GetTeacherActivityCountsData,
  GetTeachersInAcademyData,
  GetTeachersInAcademyParams,
  Pageable,
  PatchTeacherByIdData,
  RemoveTeacherByIdData,
  TeacherActivityRequest,
  TeacherInsertRequest,
  TeacherSearchRequest,
  TeacherUpdateRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptTeacher,
  adaptTeacherActivityCount,
  adaptTeachers,
  adaptTeachersInAcademy,
} from './adapters'
import type { Teacher, TeacherActivityCount, TeacherId, TeacherInAcademy } from './schemas'

// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 teacher/controller/TeacherController.kt·request/ 로 확인.

// 생성 타입은 검색 조건과 Spring Pageable 을 객체로 그리지만 실제 쿼리는 name·present·page·size·sort 를 평탄하게
// 받는다. 검색 조건은 서버가 nullable 로 받는다(TeacherSearchRequest).
export type TeacherListParams = WithNullableOptionals<TeacherSearchRequest> & Pageable

export const getTeachers = async (
  params: TeacherListParams,
  meta?: RequestMeta,
): Promise<Slice<Teacher>> => {
  const body = await httpClient().get<CommonResponseSliceTeacherResponse>('/teachers', {
    params,
    meta,
  })
  return adaptTeachers(body.data)
}

export const getTeacher = async (teacherId: TeacherId, meta?: RequestMeta): Promise<Teacher> => {
  const body = await httpClient().get<CommonResponseTeacherResponse>(`/teachers/${teacherId}`, {
    meta,
  })
  return adaptTeacher(body.data)
}

export type TeacherCreateBody = TeacherInsertRequest

export const createTeacher = async (body: TeacherCreateBody, meta?: RequestMeta): Promise<void> => {
  await httpClient().post<CreateTeacherData>('/teachers', body, { meta })
}

export type TeacherUpdateBody = TeacherUpdateRequest

export const updateTeacher = async (
  teacherId: TeacherId,
  body: TeacherUpdateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<PatchTeacherByIdData>(`/teachers/${teacherId}`, body, { meta })
}

export const deleteTeachers = async (
  teacherIds: TeacherId[],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<RemoveTeacherByIdData>('/teachers', { data: teacherIds, meta })
}

// startDatetime·endDatetime 은 서버 요청 클래스의 계산 프로퍼티가 스펙에 새어 나온 것이라 보내지 않는다.
// 날짜를 빼면 서버 기본 기간으로 센다(TeacherActivityRequest).
export type TeacherActivityParams = Omit<TeacherActivityRequest, 'startDatetime' | 'endDatetime'>

/** 로그인한 선생님의 활동 수. 응답은 CommonResponse 봉투 없이 본문 그대로다 */
export const getTeacherActivityCount = async (
  params: TeacherActivityParams,
  meta?: RequestMeta,
): Promise<TeacherActivityCount> => {
  const body = await httpClient().get<GetTeacherActivityCountsData>('/teachers/activity-counts', {
    params,
    meta,
  })
  return adaptTeacherActivityCount(body)
}

// 학원은 쿼리로 받지 않고 로그인 정보에서만 읽는다. 검색어는 nullable 이고, 응답은 CommonResponse 봉투 없이 본문
// 그대로다(TeacherInAcademyListResponse).
export type TeachersInAcademyParams = WithNullableOptionals<GetTeachersInAcademyParams>

export const getTeachersInAcademy = async (
  params?: TeachersInAcademyParams,
  meta?: RequestMeta,
): Promise<TeacherInAcademy[]> =>
  adaptTeachersInAcademy(
    await httpClient().get<GetTeachersInAcademyData>('/teachers/in-academy', { params, meta }),
  )
